import GL from "_src/engine/gl";
import compat from "_src/engine/compat";
import {memoryType, RBIntFormat} from "_src/engine/constants";
import extensions from "_src/engine/extensions";
import output from "_src/engine/output";
import resourceManager from "_src/render_graph/resource_manager";
import memoryProfiler from "_src/engine/memory_profiler";

export default class FrameBuffer {

  constructor(colorAttachments, depthAttachment, stencilAttachment) {

    this.fbo = null;

    this.colorAttachments = colorAttachments;
    this.depthAttachment = depthAttachment;

    const renderToScreen = !colorAttachments;
    if (!renderToScreen) {

      if (colorAttachments.length > compat.maxColorAttachments) {
        output.error(output.INITIALIZATION_ERROR, "Unsupported number of color attachments for current platform. " +
          "Max number is " + compat.maxColorAttachments + ", but " + colorAttachments.length + " given.");
      }

      this._createFramebuffer();
      this._bindAttachments(colorAttachments, depthAttachment, stencilAttachment);

      for (const attachment of colorAttachments) {
        resourceManager.incBufferLinkCount(attachment);
      }

      if (depthAttachment) {
        resourceManager.incBufferLinkCount(depthAttachment);
      }

      this.reportMemoryUsage();
    }

  }

  bind(gl) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);
  }

  unbind(gl) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  _createFramebuffer() {

    const gl = GL.getContext();

    this.fbo = gl.createFramebuffer();

  }

  _bindAttachments(colorAttachments, depthAttachment, stencilAttachment) {

    const gl = GL.getContext();

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbo);

    const mrtUsed = compat.mrtEnabled && colorAttachments.length > 1;
    const mrt = compat.mrtEnabled ? extensions.get("WEBGL_draw_buffers") : null;

    const targets = [];

    const attachmentsCount = mrt ? colorAttachments.length : 1;

    // color
    for (let i = 0; i < attachmentsCount; ++i) {

      const target = mrtUsed ? mrt.COLOR_ATTACHMENT0 + i : gl.COLOR_ATTACHMENT0;
      const colorAttachment = colorAttachments[i];

      if (colorAttachment.isTexture) {
        gl.framebufferTexture2D(gl.FRAMEBUFFER, target, gl.TEXTURE_2D,
                colorAttachment.pixelBuffer, 0);

      } else {
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, target,
                gl.RENDERBUFFER, colorAttachment.pixelBuffer);
      }

      targets.push(target);

    }

    if (mrtUsed) {
      mrt.drawBuffers(targets);
    }

    // depth
    if (depthAttachment) {

      if (depthAttachment.isTexture) {
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.TEXTURE_2D,
                  depthAttachment.pixelBuffer, 0);
      } else if (checkRenderBufferUseStencil(depthAttachment.format)) {
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT,
                  gl.RENDERBUFFER, depthAttachment.pixelBuffer);
      } else {
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT,
                  gl.RENDERBUFFER, depthAttachment.pixelBuffer);
      }

      if (stencilAttachment) {
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.STENCIL_ATTACHMENT,
                  gl.RENDERBUFFER, stencilAttachment.pixelBuffer);
      }

    }

    const framebufferStatus = gl.checkFramebufferStatus(gl.FRAMEBUFFER);

    switch(framebufferStatus) {
      case gl.FRAMEBUFFER_INCOMPLETE_ATTACHMENT:
        output.error(output.DEVELOPERS_ERROR, "Wrong framebuffer: FRAMEBUFFER_INCOMPLETE_ATTACHMENT");
        break;
      case gl.FRAMEBUFFER_INCOMPLETE_DIMENSIONS:
        output.error(output.DEVELOPERS_ERROR, "Wrong framebuffer: FRAMEBUFFER_INCOMPLETE_DIMENSIONS");
        break;
      case gl.FRAMEBUFFER_INCOMPLETE_MISSING_ATTACHMENT:
        output.error(output.DEVELOPERS_ERROR, "Wrong framebuffer: FRAMEBUFFER_INCOMPLETE_MISSING_ATTACHMENT");
        break;
      case gl.FRAMEBUFFER_UNSUPPORTED:
        output.error(output.INITIALIZATION_ERROR, "Wrong framebuffer: FRAMEBUFFER_UNSUPPORTED");
        break;
      case gl.FRAMEBUFFER_INCOMPLETE_MULTISAMPLE:
        output.error(output.DEVELOPERS_ERROR, "Wrong framebuffer: FRAMEBUFFER_INCOMPLETE_MULTISAMPLE");
        break;
      case gl.RENDERBUFFER_SAMPLES:
        output.error(output.DEVELOPERS_ERROR, "Wrong framebuffer: RENDERBUFFER_SAMPLES");
        break;
      default:
        // Assuming it is gl.FRAMEBUFFER_COMPLETE:
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  }

  resize(width, height) {

    if (this.colorAttachments) {
      for (let colorAttachment of this.colorAttachments) {
        if (colorAttachment.width != width || colorAttachment.width != height) {
          colorAttachment.resize(width, height);
        }
      }
    }

    if (this.depthAttachment) {
      if (this.depthAttachment.width != width || this.depthAttachment.width != height) {
        this.depthAttachment.resize(width, height);
      }
    }

    this.reportMemoryUsage();

  }

  reportMemoryUsage(sceneOwner) {
    if (this.colorAttachments) {
      for (const colorAttachment of this.colorAttachments) {
        memoryProfiler.markMemoryAllocated(colorAttachment.pixelBuffer, memoryType.GPU_FRAMEBUFFERS, colorAttachment.width * colorAttachment.height * 4, sceneOwner);
      }
    }

    if (this.depthAttachment) {
      memoryProfiler.markMemoryAllocated(this.depthAttachment.pixelBuffer, memoryType.GPU_FRAMEBUFFERS, this.depthAttachment.width * this.depthAttachment.height * 4, sceneOwner);
    }
  }

  reportMemoryRelease(sceneOwner) {
    if (this.colorAttachments) {
      for (const colorAttachment of this.colorAttachments) {
        memoryProfiler.markMemoryReleased(colorAttachment.pixelBuffer, sceneOwner);
      }
    }

    if (this.depthAttachment) {
      memoryProfiler.markMemoryReleased(this.depthAttachment.pixelBuffer, sceneOwner);
    }
  }

  size() {
    let attachment = null;
    let width = 0, height = 0;
    if (this.colorAttachments.length > 0){
      attachment = this.colorAttachments[0];
    } else if (this.depthAttachment) {
      attachment = this.depthAttachment
    }
    if (attachment) {
      width = attachment.width;
      height = attachment.height;
    }
    return [width, height];
  }

  cleanup() {

    const colorAttachments = this.colorAttachments;
    const depthAttachment = this.depthAttachment;

    if (colorAttachments) {
      for (let i = 0; i < colorAttachments.length; ++i) {

        const attachment = colorAttachments[i];

        resourceManager.decBufferLinkCount(attachment);

        const linkCount = resourceManager.getBufferCount(attachment);

        if (linkCount == 0) {
          memoryProfiler.markMemoryReleased(attachment.pixelBuffer);
          attachment.cleanup();
        }
      }
      this.colorAttachments.length = 0;
    }

    if (depthAttachment) {
      resourceManager.decBufferLinkCount(depthAttachment);

      const linkCount = resourceManager.getBufferCount(depthAttachment);

      if (linkCount == 0) {
        memoryProfiler.markMemoryReleased(depthAttachment.pixelBuffer);
        depthAttachment.cleanup();
      }
    }
    this.depthAttachment = null;
  }

}

function checkRenderBufferUseStencil(format) {
  return format == RBIntFormat.DS || format == RBIntFormat.D24S8;
}
